
import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import { MathBlockExtension } from './tiptap/MathBlockExtension';
import { MathInlineExtension } from './tiptap/MathInlineExtension';

interface InlineEditableContentProps {
  content: string;
  onContentChange: (newContent: string) => void;
  onMathDoubleClick: (initialValue: string, onSave: (newValue: string) => void) => void;
}

const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const processStringToHtmlPart = (textPart: string): string => {
    if (!textPart) return '';
    // Enhanced Regex to capture both $...$ and \(...\)
    const inlineRegex = /(\$[\s\S]*?\$|\\\(.*\\\))/g;
    const parts = textPart.split(inlineRegex).filter(Boolean);
    return parts.map(part => {
        if ((part.startsWith('$') && part.endsWith('$')) || (part.startsWith('\\(') && part.endsWith('\\)'))) {
            // Extract content: remove outer delimiters
            let latexContent = '';
            if (part.startsWith('$')) {
                latexContent = part.substring(1, part.length - 1);
            } else {
                latexContent = part.substring(2, part.length - 2);
            }
            return `<math-inline latex="${latexContent.replace(/"/g, '&quot;')}"></math-inline>`;
        }
        return part; 
    }).join('');
};

// Helper to convert the app's string format to Tiptap-compatible HTML
const stringToHtml = (content: string): string => {
  if (!content) return '<p></p>';
  
  // 1. Protect Display Math Blocks: Extract $$...$$ and replace with placeholders
  // This prevents internal newlines in math from being split into paragraphs
  const mathBlocks: string[] = [];
  let protectedContent = content.replace(/\$\$([\s\S]*?)\$\$/g, (match, inner) => {
      mathBlocks.push(inner); // Store purely the inner content
      return `__MATH_BLOCK_${mathBlocks.length - 1}__`;
  });

  // 2. Split by newline for regular text
  const lines = protectedContent.split('\n');
  
  // 3. Filter: Remove empty lines to prevent empty paragraphs
  const nonEmptyLines = lines.filter(line => line.trim() !== '');
  
  if (nonEmptyLines.length === 0) return '<p></p>';

  return nonEmptyLines.map(line => {
      // Split the line by the math block placeholder regex to handle cases where
      // a placeholder might be embedded in text (e.g. "Text __MATH_BLOCK_0__")
      // even though display math is usually on its own line.
      const parts = line.split(/(__MATH_BLOCK_\d+__)/g).filter(Boolean);
      
      return parts.map(part => {
          const blockMatch = part.match(/^__MATH_BLOCK_(\d+)__$/);
          if (blockMatch) {
              const index = parseInt(blockMatch[1]);
              const latexContent = mathBlocks[index];
              return `<math-block latex="${latexContent.replace(/"/g, '&quot;')}"></math-block>`;
          }
          // Process inline text/math and wrap in paragraph
          return `<p>${processStringToHtmlPart(part)}</p>`;
      }).join('');
  }).join('');
};

const InlineEditableContent: React.FC<InlineEditableContentProps> = ({ content, onContentChange, onMathDoubleClick }) => {
  
  const debouncedUpdate = useMemo(
    () => debounce((newContent: string) => {
        onContentChange(newContent);
    }, 500),
    [onContentChange]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      MathBlockExtension.configure({ onMathDoubleClick }),
      MathInlineExtension.configure({ onMathDoubleClick }),
    ],
    content: stringToHtml(content), // Initial content with filtering
    editorProps: {
        attributes: {
            class: 'focus:outline-none max-w-none prose prose-sm',
        },
    },
    onUpdate: ({ editor }) => {
        // Custom serialization to preserve $$ math $$
        let text = '';
        editor.state.doc.forEach((node, offset) => {
            if (node.type.name === 'paragraph') {
                let lineText = '';
                node.content.forEach(child => {
                    if (child.type.name === 'text') lineText += child.text;
                    else if (child.type.name === 'mathInline') lineText += `$${child.attrs.latex}$`;
                });
                text += lineText + '\n';
            } else if (node.type.name === 'mathBlock') {
                // FIX: Removed forced newline (\n) after display math to prevent unwanted spacing
                text += `$$${node.attrs.latex}$$`; 
            }
        });
        debouncedUpdate(text.trim());
    },
  });

  // Watch for external content changes (e.g. data loading)
  useEffect(() => {
    if (editor && content) {
        // Check if content actually differs significantly to prevent loop/cursor jump
        if (!editor.isFocused) {
             const newHTML = stringToHtml(content);
             // Fix TS error: setContent expects boolean for emitUpdate
             // Use 'as any' to bypass strict type check as runtime expects boolean 'false'
             editor.commands.setContent(newHTML, false as any);
        }
    }
  }, [content, editor]);

  return <EditorContent editor={editor} />;
};

export default InlineEditableContent;
